import React, { useEffect, useState, useCallback } from 'react';

function PmsExpenseAnomaly({ token, selectedProperty }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/expense/ai/anomaly/results', { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load');
      setRows(data.rows || []);
      setError('');
    } catch { setError('Network error'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const runScan = async () => {
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/v1/expense/ai/anomaly/scan', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: '{}'
      });
      const data = await res.json();
      if (res.status === 503 && data.ai_disabled) {
        setMessage('AI anomaly scan is disabled for this property. Rule-based anomalies are still flagged automatically on each submit.');
        return;
      }
      if (res.status === 501) return setMessage('Phase 3 — full AI implementation pending. Rule-based flags remain active.');
      if (!res.ok) return setError(data.error || 'Scan failed');
      setMessage(`Scan complete: ${data.flagged_count || 0} anomaly(ies) flagged.`);
      loadData();
    } catch { setError('Network error'); }
  };

  const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const sevColor = (s) => ({ HIGH: '#dc2626', MEDIUM: '#f59e0b', LOW: '#0ea5e9' })[s] || '#6b7280';

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Expense Anomalies</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#fef3c7', color: '#92400e', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.7rem 1rem', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <span style={{ fontWeight: 600 }}>{rows.length} open anomaly(ies)</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <button onClick={loadData} disabled={loading} style={{ padding: '0.4rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>{loading ? 'Loading…' : 'Refresh'}</button>
          <button onClick={runScan} style={{ padding: '0.4rem 0.8rem', border: 'none', borderRadius: 6, background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Run AI Scan</button>
        </span>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: '0.55rem' }}>Detected</th>
                <th style={{ padding: '0.55rem' }}>Type</th>
                <th style={{ padding: '0.55rem' }}>Severity</th>
                <th style={{ padding: '0.55rem' }}>Source</th>
                <th style={{ padding: '0.55rem' }}>Expense</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '0.55rem' }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No open anomalies. AI scanner runs nightly, rule-based flags happen on submit.</td></tr>
              ) : rows.map(a => (
                <tr key={a.anomaly_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.5rem' }}>{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                  <td style={{ padding: '0.5rem' }}>{a.anomaly_type || '—'}</td>
                  <td style={{ padding: '0.5rem' }}><span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 999, background: sevColor(a.severity) + '22', color: sevColor(a.severity), fontWeight: 600 }}>{a.severity || '—'}</span></td>
                  <td style={{ padding: '0.5rem' }}>{a.source === 'AI' ? '🤖 AI' : '📏 Rule'}</td>
                  <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.78rem' }}>{a.expense_id || '—'}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtMoney(a.amount)}</td>
                  <td style={{ padding: '0.5rem', color: '#6b7280' }}>{a.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PmsExpenseAnomaly;
